/* ===========================================================
   sync/words.js — your own dictionary and autocorrect reports
   (worker/words.js). The dictionary is kept on this device too, so
   autocorrect knows your words offline; reports are counted up here
   and sent in small batches (no doc text, just the word and the fix).
   =========================================================== */

import { signal } from '@preact/signals';
import { api } from '../lib/api.js';
import { session } from '../state.js';

const key = () => `dp:words:${session.value.user?.id || 'x'}`;
const readLocal = () => { try { return JSON.parse(localStorage.getItem(key()) || '[]'); } catch { return []; } };
const saveLocal = list => { try { localStorage.setItem(key(), JSON.stringify(list)); } catch { /* full or private */ } };

/** Your words, lower-cased, for quick checks. */
export const myWords = signal(new Set());
export const myWordList = signal([]);

function set(list) {
  const uniq = [...new Set(list)].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  myWordList.value = uniq;
  myWords.value = new Set(uniq.map(w => w.toLowerCase()));
  saveLocal(uniq);
}

let loaded = false;
export async function loadWords() {
  if (!loaded) { loaded = true; set(readLocal()); }
  try { set((await api('/api/words')).words); } catch { /* offline: this device's copy */ }
}

export const isMine = w => myWords.value.has(String(w).toLowerCase());

export async function addWord(word, from = null) {
  const w = String(word).trim();
  if (!w) return;
  set([...myWordList.value, w]);
  if (from) report(w, from, 'added');
  try { await api('/api/words', { method: 'POST', body: { word: w } }); } catch (err) { if (err.status && err.status < 500) { set(myWordList.value.filter(x => x !== w)); throw err; } }
}

export async function removeWord(word) {
  set(myWordList.value.filter(x => x !== word));
  try { await api(`/api/words/${encodeURIComponent(word)}`, { method: 'DELETE' }); } catch { /* next load fixes it */ }
}

/* ---------- reports ---------- */
let queue = [], timer = null;
export function report(word, fix, action) {
  if (!session.value.user) return;
  queue.push({ word: String(word).slice(0, 40), fix: String(fix || '').slice(0, 40), action });
  clearTimeout(timer);
  timer = setTimeout(sendReports, 20_000);
}
function sendReports() {
  if (!queue.length) return;
  const events = queue.splice(0, 50);
  api('/api/proof/report', { method: 'POST', body: { events } }).catch(() => { /* reports are best-effort */ });
}
if (typeof document !== 'undefined') document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') sendReports(); });
